import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  FaCloudUploadAlt, 
  FaRegFilePdf, 
  FaRegFileWord, 
  FaRegFileImage, 
  FaRegFileAlt,
  FaTrashAlt, 
  FaEye, 
  FaHistory, 
  FaFilter,
  FaTimes,
  FaSearch
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getDocuments, uploadDocuments, deleteDocument } from '../../api/documentApi';
import api from '../../api/axios';
import './DocumentManagement.css';

const DocumentManagement = ({ userRole = 'admin' }) => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('property_id');
  const unitId = searchParams.get('unit_id');
  const entityType = searchParams.get('type') || 'property';

  // --- Main Document State ---
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // --- UI States ---
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);
  
  // --- Upload Modal States ---
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileInputRef = useRef(null);

  // --- Fetch Documents on Mount and when IDs change ---
  const fetchDocuments = useCallback(async () => {
    if (!propertyId && !unitId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getDocuments(
        propertyId ? parseInt(propertyId) : null,
        unitId ? parseInt(unitId) : null
      );
      
      // Transform API response to match component expectations
      const transformedDocs = (response.data || []).map(doc => ({
        id: doc.id,
        name: doc.name,
        category: doc.category,
        version: doc.version || 'v1.0',
        date: doc.created_at ? new Date(doc.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        type: doc.mime_type,
        url: doc.file_path ? (doc.file_path.startsWith('http') ? doc.file_path : `${api.defaults.baseURL}/${doc.file_path}`) : null
      }));

      setDocuments(transformedDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [propertyId, unitId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // --- Helpers to determine Icon based on file type ---
  const getFileIcon = (mimeType) => {
    if (!mimeType) return <FaRegFileAlt className="icon-default" />;
    if (mimeType.includes('pdf')) return <FaRegFilePdf className="icon-pdf" />;
    if (mimeType.includes('word') || mimeType.includes('document') || mimeType.includes('msword')) return <FaRegFileWord className="icon-word" />;
    if (mimeType.includes('image')) return <FaRegFileImage className="icon-img" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FaRegFileAlt className="icon-default" />;
    return <FaRegFileAlt className="icon-default" />;
  };

  // --- Handlers: Upload Flow ---

  // 1. Open File Selector
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newQueueItems = files.map(file => ({
        fileObject: file,
        tempId: Date.now() + Math.random(),
        name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for editing
        category: 'Property',
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB'
      }));
      setUploadQueue([...uploadQueue, ...newQueueItems]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // 2. Update File Details in Queue
  const updateQueueItem = (tempId, field, value) => {
    setUploadQueue(uploadQueue.map(item => 
      item.tempId === tempId ? { ...item, [field]: value } : item
    ));
  };

  // 3. Remove item from Queue
  const removeFromQueue = (tempId) => {
    setUploadQueue(uploadQueue.filter(item => item.tempId !== tempId));
  };

  // 4. Finalize Upload
  const confirmUpload = async () => {
    if (uploadQueue.length === 0) return;

    if (!propertyId && !unitId) {
      toast.error('Property or Unit ID is required');
      return;
    }

    setUploading(true);
    try {
      // Prepare FormData
      const formData = new FormData();

      // Add files
      uploadQueue.forEach(item => {
        formData.append('files[]', item.fileObject);
      });

      // Add names
      uploadQueue.forEach(item => {
        formData.append('names[]', item.name);
      });

      // Add categories
      uploadQueue.forEach(item => {
        formData.append('categories[]', item.category);
      });

      // Add property_id or unit_id
      if (propertyId) {
        formData.append('property_id', propertyId);
      }
      if (unitId) {
        formData.append('unit_id', unitId);
      }

      await uploadDocuments(formData);
      toast.success('Documents uploaded successfully!');
      
      // Clear queue and close modal
      setUploadQueue([]);
      setIsUploadModalOpen(false);
      
      // Refresh documents list
      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error(error.response?.data?.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  // --- Handlers: Main List ---
  const deleteDoc = async (id) => {
    if (userRole !== 'admin') {
      toast.error('Access Denied');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteDocument(id);
      toast.success('Document deleted successfully!');
      // Refresh documents list
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const filteredDocs = documents.filter(doc => {
    const matchesCategory = filter === 'All' || doc.category === filter;
    const matchesSearch = doc.name.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get entity name for display
  const getEntityName = () => {
    if (propertyId) return `Property ID: ${propertyId}`;
    if (unitId) return `Unit ID: ${unitId}`;
    return '';
  };

  return (
    <div className="doc-wrapper">
      <div className="doc-container">
        
        {/* Header */}
        <header className="doc-header">
          <div className="header-text">
            <h1>Document Management</h1>
            <p>
              {propertyId || unitId 
                ? `Documents for ${entityType === 'property' ? 'Property' : 'Unit'} - ${getEntityName()}`
                : 'Securely store, categorize, and track land-related documentation.'
              }
            </p>
          </div>
        </header>

        {/* Controls */}
        <div className="doc-controls">
          {(propertyId || unitId) && (
            <button 
              className="upload-btn" 
              onClick={() => setIsUploadModalOpen(true)}
              disabled={!propertyId && !unitId}
            >
              <FaCloudUploadAlt className="upload-icon" /> Upload Documents
            </button>
          )}

          <div className="filter-wrapper">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="filter-box">
              <FaFilter className="filter-icon" />
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="All">All Categories</option>
                <option value="Property">Property</option>
                <option value="Lease">Lease</option>
                <option value="Maintenance">Maintenance</option>
              </select>
            </div>
          </div>
        </div>

        {/* Document Table */}
        <div className="table-card">
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading documents...</p>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>No documents found. {propertyId || unitId ? 'Upload documents to get started.' : 'Please select a property or unit to view documents.'}</p>
            </div>
          ) : (
            <table className="doc-table">
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Category</th>
                  <th>Version</th>
                  <th>Date Added</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map(doc => (
                  <tr key={doc.id}>
                    <td className="doc-name-cell">
                      <div className="icon-box-sm">
                        {getFileIcon(doc.type)}
                      </div>
                      {doc.name}
                    </td>
                    <td><span className={`tag tag-${doc.category.toLowerCase()}`}>{doc.category}</span></td>
                    <td><span className="version-pill"><FaHistory /> {doc.version}</span></td>
                    <td className="date-text">{doc.date}</td>
                    <td className="actions-cell">
                      {doc.url && (
                        <button className="btn-icon view" onClick={() => setViewingDoc(doc)}><FaEye /></button>
                      )}
                      {userRole === 'admin' && (
                        <button className="btn-icon delete" onClick={() => deleteDoc(doc.id)}><FaTrashAlt /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* --- UPLOAD MODAL --- */}
      {isUploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content upload-modal-size">
            <div className="modal-header">
              <h3>Upload Documents</h3>
              <button className="close-btn" onClick={() => setIsUploadModalOpen(false)}><FaTimes /></button>
            </div>
            
            <div className="modal-body">
              {/* Drag & Drop Area / Select Button */}
              <div 
                className="upload-drop-zone"
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer' }}
              >
                <FaCloudUploadAlt size={40} color="#cbd5e1" />
                <p>Drag files here or click to select</p>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  multiple 
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  className="file-input-hidden"
                  style={{ display: 'none' }}
                />
              </div>

              {/* Upload Queue List */}
              {uploadQueue.length > 0 && (
                <div className="upload-queue">
                  <h4>Files to Upload ({uploadQueue.length})</h4>
                  <div className="queue-list">
                    {uploadQueue.map((item) => (
                      <div className="queue-item" key={item.tempId}>
                        <div className="queue-icon">
                          {getFileIcon(item.fileObject.type)}
                        </div>
                        
                        <div className="queue-inputs">
                          <input 
                            type="text" 
                            className="input-rename"
                            value={item.name}
                            onChange={(e) => updateQueueItem(item.tempId, 'name', e.target.value)}
                            placeholder="File Name"
                          />
                          <div className="queue-meta">
                            <span className="file-size">{item.size}</span>
                            <select 
                              className="select-category"
                              value={item.category} 
                              onChange={(e) => updateQueueItem(item.tempId, 'category', e.target.value)}
                            >
                              <option value="Property">Property</option>
                              <option value="Lease">Lease</option>
                              <option value="Maintenance">Maintenance</option>
                            </select>
                          </div>
                        </div>

                        <button 
                          className="btn-remove"
                          onClick={() => removeFromQueue(item.tempId)}
                          title="Remove from queue"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setIsUploadModalOpen(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={confirmUpload}
                disabled={uploadQueue.length === 0 || uploading}
              >
                {uploading ? 'Uploading...' : `Upload ${uploadQueue.length > 0 ? `${uploadQueue.length} Files` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- VIEW MODAL (Supports PDF & Image) --- */}
      {viewingDoc && (
        <div className="modal-overlay">
          <div className="modal-content view-modal-size">
            <div className="modal-header">
              <h3>{viewingDoc.name}</h3>
              <button className="close-btn" onClick={() => setViewingDoc(null)}><FaTimes /></button>
            </div>
            <div className="view-body">
              {viewingDoc.type && viewingDoc.type.includes('image') ? (
                <img src={viewingDoc.url} alt={viewingDoc.name} className="view-img" />
              ) : viewingDoc.type && viewingDoc.type.includes('pdf') ? (
                <iframe src={viewingDoc.url} title="PDF Viewer" className="view-iframe"></iframe>
              ) : (
                <div className="no-preview">
                  <FaRegFileAlt size={60} color="#cbd5e1" />
                  <p>Preview not available for this file type.</p>
                  {viewingDoc.url && (
                    <a href={viewingDoc.url} download={viewingDoc.name} className="btn-primary">Download File</a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;
